import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash, UploadSimple, Copy, Eye } from "@phosphor-icons/react";
import { QRCodeSVG } from "qrcode.react";
import { Switch } from "@/components/ui/switch";
import { Shell } from "@/components/Shell";
import { WriteTagButton } from "@/components/WriteTagButton";
import { AvatarDisplay, MotifPicker } from "@/components/Avatar";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";
import { copyText } from "@/lib/clipboard";

export default function BuddyEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, user } = useApp();
  const canEdit = !(user?.family_role === "viewer" && !user?.is_family_owner);
  const [b, setB] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showCallHint, setShowCallHint] = useState(false);
  const fileRef = useRef();

  useEffect(() => { api.get(`/tags/${id}`).then((r) => setB(r.data)).catch(() => navigate("/tags")); }, [id]);
  useEffect(() => {
    if (!b) return;
    const hasPhone = (b.contacts || []).some((c) => (c.phone || "").trim());
    if (!hasPhone && b.allow_calls) { setB((prev) => ({ ...prev, allow_calls: false })); setShowCallHint(true); }
  }, [b]);

  if (!b) return <Shell showNav={false}><div className="pt-20 text-center font-heading text-gray-500">…</div></Shell>;

  const buddyUrl = `${window.location.origin}/t/${b.code}`;
  const set = (k, v) => setB({ ...b, [k]: v });
  const useDefault = b.use_default_profile;

  const addContact = () => setB({ ...b, contacts: [...(b.contacts || []), { id: crypto.randomUUID(), name: "", relationship: "", phone: "" }] });
  const contactsSupported = typeof navigator !== "undefined" && "contacts" in navigator && "ContactsManager" in window;
  const fromPhonebook = async () => {
    try {
      const r = await navigator.contacts.select(["name", "tel"], { multiple: false });
      if (r && r[0]) {
        const n = Array.isArray(r[0].name) ? r[0].name[0] : r[0].name;
        const p = Array.isArray(r[0].tel) ? r[0].tel[0] : r[0].tel;
        setB((prev) => ({ ...prev, contacts: [...(prev.contacts || []), { id: crypto.randomUUID(), name: n || "", relationship: "", phone: p || "" }] }));
      }
    } catch { /* denied */ }
  };
  const updContact = (i, k, v) => { const c = [...b.contacts]; c[i] = { ...c[i], [k]: v }; setB({ ...b, contacts: c }); };
  const rmContact = (i) => setB({ ...b, contacts: b.contacts.filter((_, idx) => idx !== i) });

  const onUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post("/profile/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setB({ ...b, avatar_type: "upload", avatar_value: res.data.path });
      toast.success("Bild hochgeladen");
    } catch { toast.error("Upload fehlgeschlagen"); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/tags/${id}/profile`, {
        label: b.label, use_default_profile: b.use_default_profile,
        display_name: b.display_name, avatar_type: b.avatar_type, avatar_value: b.avatar_value,
        contacts: b.contacts, notes: b.notes,
        allow_calls: b.allow_calls, allow_messages: b.allow_messages,
      });
      setB(res.data);
      toast.success(t("buddyUpdated"));
      navigate("/tags");
    } catch { toast.error("Fehler"); }
    finally { setSaving(false); }
  };

  return (
    <Shell showNav={false}>
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">
        <h1 className="text-2xl font-heading font-semibold text-gray-900">{t("buddyProfile")}</h1>

        {!canEdit && (
          <p data-testid="buddyedit-readonly-note" className="text-sm font-body text-gray-600 bg-gray-100 border-2 border-gray-800 rounded-2xl px-4 py-3">{t("viewerReadOnly")}</p>
        )}

        <div className="sb-card p-5 space-y-3">
          <div>
            <label className="text-xs font-body text-gray-500 uppercase tracking-wider">{t("buddyName")}</label>
            <input data-testid="buddyedit-label" className="sb-input mt-1" placeholder={t("buddyNamePlaceholder")} value={b.label || ""} onChange={(e) => set("label", e.target.value)} />
          </div>
          <div className="flex items-center gap-3 pt-2 border-t border-gray-200">
            <QRCodeSVG value={buddyUrl} size={52} className="shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-body text-gray-500 uppercase tracking-wider">{t("generatedUrl")}</p>
              <p className="text-sm font-body text-gray-700 break-all">/t/{b.code}</p>
            </div>
            <button data-testid="buddyedit-preview" onClick={() => window.open(`${buddyUrl}?preview=1`, "_blank")}
              className="p-2 rounded-xl border-2 border-gray-800 bg-white active:scale-90"><Eye size={18} weight="bold" /></button>
            <button data-testid="buddyedit-copy-url" onClick={async () => { await copyText(buddyUrl); toast.success(t("urlCopied")); }}
              className="p-2 rounded-xl border-2 border-gray-800 bg-white active:scale-90"><Copy size={18} weight="bold" /></button>
          </div>
          <WriteTagButton tag={b} className="sb-btn w-full py-3 mt-3 text-white font-medium flex items-center justify-center gap-2" />
        </div>

        <div className="sb-card p-5">
          <p className="text-xs font-body text-gray-500 uppercase tracking-wider mb-2">{t("dataSource")}</p>
          <div className="grid grid-cols-1 gap-2">
            <button type="button" data-testid="buddyedit-default" onClick={() => set("use_default_profile", true)}
              className={`w-full text-left p-3 rounded-xl border-2 border-gray-800 ${useDefault ? "text-white" : "bg-white text-gray-800"}`} style={useDefault ? { background: "var(--app-primary)" } : {}}>
              <span className="font-heading font-medium text-sm block">{t("sourceDefaultTitle")}</span>
              <span className={`text-xs font-body block mt-0.5 ${useDefault ? "text-white/90" : "text-gray-500"}`}>{t("sourceDefaultDesc")}</span>
            </button>
            <button type="button" data-testid="buddyedit-custom" onClick={() => set("use_default_profile", false)}
              className={`w-full text-left p-3 rounded-xl border-2 border-gray-800 ${!useDefault ? "text-white" : "bg-white text-gray-800"}`} style={!useDefault ? { background: "var(--app-primary)" } : {}}>
              <span className="font-heading font-medium text-sm block">{t("sourceCustomTitle")}</span>
              <span className={`text-xs font-body block mt-0.5 ${!useDefault ? "text-white/90" : "text-gray-500"}`}>{t("sourceCustomDesc")}</span>
            </button>
          </div>
          {useDefault && (
            <button data-testid="buddyedit-edit-profile" onClick={() => navigate("/profile")}
              className="mt-3 text-sm font-heading font-medium underline" style={{ color: "var(--app-strong)" }}>{t("editMyProfile")} →</button>
          )}
        </div>

        {!useDefault && (
          <>
            <div className="sb-card p-5">
              <div className="flex items-center gap-4">
                <AvatarDisplay type={b.avatar_type} value={b.avatar_value} size={72} />
                <div className="flex-1">
                  <label className="text-xs font-body text-gray-500 uppercase tracking-wider">{t("displayName")}</label>
                  <input data-testid="buddyedit-displayname" className="sb-input mt-1" placeholder={t("displayName")} value={b.display_name || ""} onChange={(e) => set("display_name", e.target.value)} />
                </div>
              </div>
              <p className="text-xs font-body text-gray-500 uppercase tracking-wider mt-4 mb-2">{t("buddyAvatar")}</p>
              <MotifPicker selectedType={b.avatar_type} selectedValue={b.avatar_value} onSelect={(type, val) => setB({ ...b, avatar_type: type, avatar_value: val })} testidPrefix="buddyedit-motif" />
              <div className="mt-2">
                <button data-testid="buddyedit-upload" onClick={() => fileRef.current?.click()} className="sb-btn px-4 py-2 bg-white font-medium text-sm flex items-center gap-2"><UploadSimple size={18} weight="bold" />{t("uploadImage")}</button>
                <input ref={fileRef} type="file" accept="image/*" hidden onChange={onUpload} />
              </div>
            </div>

            <div className="sb-card p-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-heading font-semibold">{t("emergencyContacts")}</h2>
                <div className="flex items-center gap-2">
                  {contactsSupported && <button type="button" data-testid="buddyedit-phonebook" onClick={fromPhonebook} className="sb-btn px-2.5 py-1.5 bg-white text-xs">{t("fromPhonebook")}</button>}
                  <button data-testid="buddyedit-add-contact" onClick={addContact} className="p-2 rounded-xl border-2 border-gray-800 text-white active:scale-90" style={{ background: "var(--app-primary)" }}><Plus size={18} weight="bold" /></button>
                </div>
              </div>
              <div className="space-y-4">
                {(b.contacts || []).map((c, i) => (
                  <div key={c.id || i} className="space-y-2 pb-4 border-b border-gray-200 last:border-0">
                    <div className="flex gap-2">
                      <input className="sb-input" placeholder={t("name")} value={c.name} onChange={(e) => updContact(i, "name", e.target.value)} data-testid={`buddyedit-contact-name-${i}`} />
                      <button onClick={() => rmContact(i)} className="p-3 rounded-xl border-2 border-gray-800 bg-white text-red-500 active:scale-90"><Trash size={18} weight="bold" /></button>
                    </div>
                    <input className="sb-input" placeholder={t("relationship")} value={c.relationship} onChange={(e) => updContact(i, "relationship", e.target.value)} data-testid={`buddyedit-contact-rel-${i}`} />
                    <input className="sb-input" placeholder={t("phone")} value={c.phone} onChange={(e) => updContact(i, "phone", e.target.value)} data-testid={`buddyedit-contact-phone-${i}`} />
                    <p className="text-xs font-body text-gray-400">{t("contactPhoneHint")}</p>
                  </div>
                ))}
                {(!b.contacts || b.contacts.length === 0) && <p className="font-body text-gray-500 text-sm">—</p>}
              </div>
            </div>

            <div className="sb-card p-5 space-y-3">
              <div>
                <label className="text-xs font-body text-gray-500 uppercase tracking-wider">{t("notes")}</label>
                <textarea data-testid="buddyedit-notes" className="sb-input mt-1" rows={3} value={b.notes || ""} onChange={(e) => set("notes", e.target.value)} />
              </div>
            </div>
          </>
        )}

        <div className="sb-card p-5 space-y-3">
          <p className="text-xs font-body text-gray-500 uppercase tracking-wider">{t("contactOptions")}</p>
          <div className="flex items-center justify-between">
            <span className="font-body">{t("buddyActive")}</span>
            <Switch data-testid="buddyedit-active" checked={b.status === "ACTIVE"} onCheckedChange={async (v) => {
              const res = await api.post(`/tags/${id}/status`, { status: v ? "ACTIVE" : "INACTIVE" });
              setB({ ...b, status: res.data.status });
              toast.success(t("saved"));
            }} />
          </div>
          {(() => {
            const hasPhone = (b.contacts || []).some((c) => (c.phone || "").trim());
            return (
              <>
                <div className="flex items-center justify-between">
                  <span className="font-body">{t("allowCalls")}</span>
                  <div onClick={() => { if (!hasPhone) setShowCallHint(true); }}>
                    <Switch data-testid="buddyedit-allow-calls" disabled={!hasPhone} checked={!!b.allow_calls}
                      onCheckedChange={(v) => { set("allow_calls", v); setShowCallHint(false); }} />
                  </div>
                </div>
                {!hasPhone && showCallHint && <p data-testid="buddyedit-call-hint" className="text-xs font-body text-red-500 -mt-1">Bitte zuerst eine Rufnummer unter Kontakte hinterlegen.</p>}
                {hasPhone && b.allow_calls && <p className="text-xs font-body text-gray-500 -mt-1">{t("callHint")}</p>}
              </>
            );
          })()}
          <div className="flex items-center justify-between">
            <span className="font-body">{t("allowMessages")}</span>
            <Switch data-testid="buddyedit-allow-messages" checked={!!b.allow_messages} onCheckedChange={(v) => set("allow_messages", v)} />
          </div>
          {b.allow_messages && <p className="text-xs font-body text-gray-500 -mt-1">{t("messageEmailHint")}</p>}
        </div>

        {b && b.last_modified_by && (
          <p data-testid="buddy-last-modified" className="text-xs font-body text-gray-400 text-center">{t("lastModifiedBy")} {b.last_modified_by}{b.last_modified_at ? ` · ${new Date(b.last_modified_at).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}` : ""}</p>
        )}

        {canEdit && (
          <button data-testid="buddyedit-save" onClick={save} disabled={saving} className="sb-btn w-full py-4 text-white font-medium text-lg disabled:opacity-60" style={{ background: "var(--app-primary)" }}>
            {saving ? "…" : t("save")}
          </button>
        )}
      </motion.div>
    </Shell>
  );
}
